import {
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser'
import type { AuthProvider, AuthUser } from './auth-provider'

function getB2CConfig() {
  const tenant = import.meta.env['VITE_B2C_TENANT']
  const tenantDomain = import.meta.env['VITE_B2C_TENANT_DOMAIN']
  const clientId = import.meta.env['VITE_B2C_CLIENT_ID']

  if (!clientId || !tenant || !tenantDomain) {
    throw new Error(
      'Azure B2C is not configured. Set VITE_B2C_TENANT, VITE_B2C_TENANT_DOMAIN, ' +
      'and VITE_B2C_CLIENT_ID in your .env.local file. See .env.example for reference.',
    )
  }
  const signInPolicy = import.meta.env['VITE_B2C_SIGNUP_SIGNIN_POLICY'] as string
  const editProfilePolicy = import.meta.env['VITE_B2C_EDIT_PROFILE_POLICY'] as string
  const apiScope = import.meta.env['VITE_B2C_API_SCOPE'] as string
  const redirectUri = import.meta.env['VITE_B2C_REDIRECT_URI'] as string | undefined

  return {
    tenant,
    tenantDomain,
    clientId,
    signInPolicy,
    editProfilePolicy,
    apiScope,
    authority: `https://${tenantDomain}/${tenant}.onmicrosoft.com/${signInPolicy}`,
    editProfileAuthority: `https://${tenantDomain}/${tenant}.onmicrosoft.com/${editProfilePolicy}`,
    knownAuthorities: [tenantDomain],
    redirectUri: redirectUri ?? '/app/dashboard',
    scopes: [apiScope],
  }
}

function mapClaimsToUser(account: AccountInfo): AuthUser | null {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined
  if (!claims) return null

  return {
    userId: account.homeAccountId,
    entityId: typeof claims['entityId'] === 'number' ? claims['entityId'] : 0,
    username: account.username,
    firstname: typeof claims['given_name'] === 'string' ? claims['given_name'] : '',
    lastname: typeof claims['family_name'] === 'string' ? claims['family_name'] : '',
    email: account.username,
    organizationId:
      typeof claims['organizationId'] === 'number' ? claims['organizationId'] : 0,
    // B2C does not emit an `isSuperAdmin` claim and there is no plan to make it
    // (that would mean touching the custom policies). This therefore always
    // resolves to `false`; the authoritative source is `fetchUserIdentity`,
    // which maps the `IsSuperAdmin` field of GET /api/UserIdentity. UC-049 BR7.
    isSuperAdmin: claims['isSuperAdmin'] === true,
    isAdmin: claims['isAdmin'] === true,
    permissions:
      typeof claims['permissions'] === 'object' && claims['permissions'] !== null
        ? (claims['permissions'] as Record<string, number>)
        : {},
    roles: Array.isArray(claims['roles'])
      ? (claims['roles'] as string[])
      : [],
    viewCurrencyId:
      typeof claims['viewCurrencyId'] === 'number' ? claims['viewCurrencyId'] : 0,
    paymentApprovalLevel:
      typeof claims['paymentApprovalLevel'] === 'number'
        ? claims['paymentApprovalLevel']
        : 0,
    tenants: [],
  }
}

export class AzureB2CProvider implements AuthProvider {
  private msalInstance: PublicClientApplication | null = null
  private initPromise: Promise<void> | null = null
  private authStateListeners: Array<(user: AuthUser | null) => void> = []

  async initialize(): Promise<void> {
    // Guard against React 18 StrictMode double-invoking effects.
    // Without this, the second call creates a new MSAL instance that overwrites
    // msalInstance while the first is still awaiting handleRedirectPromise().
    if (this.initPromise) return this.initPromise
    this.initPromise = this.doInitialize()
    return this.initPromise
  }

  private async doInitialize(): Promise<void> {
    const config = getB2CConfig()

    this.msalInstance = new PublicClientApplication({
      auth: {
        clientId: config.clientId,
        authority: config.authority,
        knownAuthorities: config.knownAuthorities,
        redirectUri: config.redirectUri,
        postLogoutRedirectUri: '/',
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    })

    await this.msalInstance.initialize()

    // Handle redirect result after B2C redirect
    try {
      const result = await this.msalInstance.handleRedirectPromise()
      console.debug('[Auth] handleRedirectPromise result:', result ? 'got result' : 'null')
      const accounts = this.msalInstance.getAllAccounts()
      console.debug('[Auth] accounts in cache:', accounts.length)
      const account = result?.account ?? accounts[0] ?? null
      if (account) {
        const user = mapClaimsToUser(account)
        console.debug('[Auth] mapped user:', user ? user.email : 'null (no claims)')
        this.notifyListeners(user)
      }
    } catch (err) {
      // Stale interaction state in sessionStorage can cause BrowserAuthError.
      // Clear it and fall back to cached accounts.
      console.warn('[Auth] handleRedirectPromise failed, clearing interaction state', err)
      this.clearInteractionState()
      const accounts = this.msalInstance.getAllAccounts()
      const account = accounts[0] ?? null
      if (account) {
        this.notifyListeners(mapClaimsToUser(account))
      }
    }
  }

  /** Remove stale MSAL interaction entries from sessionStorage */
  private clearInteractionState(): void {
    const keys = Object.keys(sessionStorage)
    for (const key of keys) {
      if (key.startsWith('msal.') && key.includes('interaction')) {
        sessionStorage.removeItem(key)
      }
    }
  }

  async login(): Promise<void> {
    if (!this.msalInstance) throw new Error('Auth not initialized')
    const config = getB2CConfig()

    await this.msalInstance.loginRedirect({
      scopes: config.scopes,
      prompt: 'select_account',
    })
  }

  async editProfile(): Promise<void> {
    if (!this.msalInstance) throw new Error('Auth not initialized')
    const config = getB2CConfig()
    const account = this.msalInstance.getAllAccounts()[0]
    await this.msalInstance.loginRedirect({
      authority: config.editProfileAuthority,
      scopes: config.scopes,
      account: account ?? undefined,
    })
  }

  async logout(): Promise<void> {
    if (!this.msalInstance) throw new Error('Auth not initialized')

    const account = this.msalInstance.getAllAccounts()[0]
    await this.msalInstance.logoutRedirect({
      account: account ?? undefined,
    })

    this.notifyListeners(null)
  }

  async getToken(): Promise<string | null> {
    if (!this.msalInstance) return null
    const config = getB2CConfig()
    const account = this.msalInstance.getAllAccounts()[0]
    if (!account) return null

    try {
      const result: AuthenticationResult = await this.msalInstance.acquireTokenSilent({
        scopes: config.scopes,
        account,
      })
      return result.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.msalInstance.acquireTokenRedirect({
          scopes: config.scopes,
          account,
        })
      }
      return null
    }
  }

  getUser(): AuthUser | null {
    if (!this.msalInstance) return null
    const account = this.msalInstance.getAllAccounts()[0]
    if (!account) return null
    return mapClaimsToUser(account)
  }

  isAuthenticated(): boolean {
    if (!this.msalInstance) return false
    return this.msalInstance.getAllAccounts().length > 0
  }

  onAuthStateChange(callback: (user: AuthUser | null) => void): () => void {
    this.authStateListeners.push(callback)
    return () => {
      this.authStateListeners = this.authStateListeners.filter((l) => l !== callback)
    }
  }

  private notifyListeners(user: AuthUser | null): void {
    for (const listener of this.authStateListeners) {
      listener(user)
    }
  }
}

// Suppress unused import warning — BrowserAuthError used in error handling context
void BrowserAuthError
